import type { LangMap } from "./index";

/** Mini-gioco sfide, calendario riposo e promemoria push. */
export const gamify: LangMap = {
  it: {
    checkin: {
      title: "Check-in con Lumen",
      subtitle: "Rispondi e sblocca le sfide di oggi",
      done: "Check-in fatto! Buono studio ✨",
      reward: "+10 XP",
      questions: {
        mood: "Come ti senti oggi?",
        time: "Quanto tempo hai per studiare?",
        goal: "Qual è il tuo obiettivo di oggi?",
      },
      answers: {
        great: "Carico!",
        ok: "Così così",
        tired: "Stanco",
        t15: "15 min",
        t30: "30 min",
        t60: "60+ min",
        review: "Ripassare",
        learn: "Imparare cose nuove",
        test: "Prepararmi a una verifica",
      },
      reactions: {
        great: "Fantastico! Puntiamo alla sfida più grossa di oggi 🚀",
        ok: "Va benissimo: partiamo piano con una sfida corta 💛",
        tired: "Nessun problema: 15 minuti bastano per tenere viva la serie 🌙",
        t15: "Perfetto, scegliamo una sfida lampo.",
        t30: "Ottimo ritmo: mezz'ora vale già tanti XP.",
        t60: "Wow! Oggi puoi completare tutte le sfide.",
        review: "Ripasso attivo: flashcard e quiz sono la scelta giusta.",
        learn: "Nuovi argomenti: mappe e appunti ti aiutano.",
        test: "Modalità verifica: ti guido passo passo.",
      },
    },
    game: {
      questsDone: "Sfide completate",
      todayXp: "XP di oggi",
      bonusTitle: "Bonus giornata perfetta",
      bonusHint: "Completa tutte le sfide per il bonus.",
      go: "Vai",
    },
    restCal: {
      title: "Calendario riposo",
      open: "Scegli le date",
      subtitle: "Tocca i giorni in cui non studi: la serie resta protetta.",
      weekdays: "Giorni fissi della settimana",
      dates: "Date singole",
      selected_one: "{{count}} data scelta",
      selected_other: "{{count}} date scelte",
      clear: "Pulisci",
      save: "Salva",
      cancel: "Annulla",
      saved: "Giorni di riposo aggiornati",
    },
    reminders: {
      title: "Promemoria di studio",
      open: "Notifiche",
      subtitle: "Ti avviso per studiare e non perdere la serie.",
      enable: "Attiva promemoria",
      times: "Orari",
      addTime: "Aggiungi orario",
      days: "Giorni",
      streakGuard: "Avviso serie a rischio (sera)",
      test: "Prova notifica",
      save: "Salva",
      cancel: "Annulla",
      saved: "Promemoria aggiornati",
      denied: "Permesso notifiche negato: attivalo dalle impostazioni del dispositivo.",
      unsupported: "Questo dispositivo non supporta le notifiche.",
      webHint: "Sul web le notifiche arrivano con l'app aperta; sull'app mobile anche in background.",
      pushTitle: "È ora di studiare ✨",
      pushBody: "Bastano 15 minuti con Lumen per far crescere la tua serie.",
      streakTitle: "La tua serie è a rischio 🔥",
      streakBody: "Studia ancora oggi per non perdere la streak.",
    },
  },
  en: {
    checkin: {
      title: "Check in with Lumen",
      subtitle: "Answer and unlock today's challenges",
      done: "Check-in done! Happy studying ✨",
      reward: "+10 XP",
      questions: {
        mood: "How do you feel today?",
        time: "How much time do you have?",
        goal: "What's your goal today?",
      },
      answers: {
        great: "Pumped!",
        ok: "So-so",
        tired: "Tired",
        t15: "15 min",
        t30: "30 min",
        t60: "60+ min",
        review: "Review",
        learn: "Learn new things",
        test: "Prep for a test",
      },
      reactions: {
        great: "Awesome! Let's aim for today's biggest challenge 🚀",
        ok: "That's fine: let's start with a short challenge 💛",
        tired: "No worries: 15 minutes keep your streak alive 🌙",
        t15: "Perfect, let's pick a quick challenge.",
        t30: "Great pace: half an hour is worth solid XP.",
        t60: "Wow! You can clear every challenge today.",
        review: "Active recall: flashcards and quizzes are ideal.",
        learn: "New topics: maps and notes will help.",
        test: "Test mode: I'll guide you step by step.",
      },
    },
    game: {
      questsDone: "Challenges done",
      todayXp: "XP today",
      bonusTitle: "Perfect day bonus",
      bonusHint: "Finish every challenge to get the bonus.",
      go: "Go",
    },
    restCal: {
      title: "Rest calendar",
      open: "Pick dates",
      subtitle: "Tap the days you don't study: your streak stays safe.",
      weekdays: "Fixed weekdays",
      dates: "Single dates",
      selected_one: "{{count}} date selected",
      selected_other: "{{count}} dates selected",
      clear: "Clear",
      save: "Save",
      cancel: "Cancel",
      saved: "Rest days updated",
    },
    reminders: {
      title: "Study reminders",
      open: "Notifications",
      subtitle: "I'll remind you to study and keep your streak.",
      enable: "Enable reminders",
      times: "Times",
      addTime: "Add time",
      days: "Days",
      streakGuard: "Streak-at-risk alert (evening)",
      test: "Test notification",
      save: "Save",
      cancel: "Cancel",
      saved: "Reminders updated",
      denied: "Notification permission denied: enable it in device settings.",
      unsupported: "This device doesn't support notifications.",
      webHint: "On the web reminders arrive while the app is open; on mobile also in background.",
      pushTitle: "Time to study ✨",
      pushBody: "Just 15 minutes with Lumen grows your streak.",
      streakTitle: "Your streak is at risk 🔥",
      streakBody: "Study today so you don't lose your streak.",
    },
  },
  es: {
    checkin: {
      title: "Check-in con Lumen",
      subtitle: "Responde y desbloquea los retos de hoy",
      done: "¡Check-in hecho! Buen estudio ✨",
      reward: "+10 XP",
      questions: { mood: "¿Cómo te sientes hoy?", time: "¿Cuánto tiempo tienes?", goal: "¿Cuál es tu objetivo de hoy?" },
      answers: { great: "¡Con energía!", ok: "Regular", tired: "Cansado", t15: "15 min", t30: "30 min", t60: "60+ min", review: "Repasar", learn: "Aprender cosas nuevas", test: "Preparar un examen" },
      reactions: {
        great: "¡Genial! Vamos al reto más grande de hoy 🚀",
        ok: "Perfecto: empezamos con un reto corto 💛",
        tired: "Sin problema: 15 minutos mantienen la racha 🌙",
        t15: "Elegimos un reto rápido.",
        t30: "Buen ritmo: media hora ya vale XP.",
        t60: "¡Guau! Hoy puedes completar todos los retos.",
        review: "Repaso activo: tarjetas y cuestionarios son ideales.",
        learn: "Temas nuevos: mapas y apuntes te ayudan.",
        test: "Modo examen: te guío paso a paso.",
      },
    },
    game: { questsDone: "Retos completados", todayXp: "XP de hoy", bonusTitle: "Bono día perfecto", bonusHint: "Completa todos los retos para el bono.", go: "Ir" },
    restCal: { title: "Calendario de descanso", open: "Elegir fechas", subtitle: "Toca los días que no estudias: la racha queda protegida.", weekdays: "Días fijos de la semana", dates: "Fechas concretas", selected_one: "{{count}} fecha elegida", selected_other: "{{count}} fechas elegidas", clear: "Limpiar", save: "Guardar", cancel: "Cancelar", saved: "Días de descanso actualizados" },
    reminders: { title: "Recordatorios de estudio", open: "Notificaciones", subtitle: "Te aviso para estudiar y mantener la racha.", enable: "Activar recordatorios", times: "Horarios", addTime: "Añadir horario", days: "Días", streakGuard: "Aviso de racha en riesgo (noche)", test: "Probar notificación", save: "Guardar", cancel: "Cancelar", saved: "Recordatorios actualizados", denied: "Permiso denegado: actívalo en los ajustes del dispositivo.", unsupported: "Este dispositivo no admite notificaciones.", webHint: "En la web llegan con la app abierta; en móvil también en segundo plano.", pushTitle: "Hora de estudiar ✨", pushBody: "Con 15 minutos con Lumen tu racha crece.", streakTitle: "Tu racha está en riesgo 🔥", streakBody: "Estudia hoy para no perder la racha." },
  },
  fr: {
    checkin: {
      title: "Check-in avec Lumen",
      subtitle: "Réponds et débloque les défis du jour",
      done: "Check-in fait ! Bon travail ✨",
      reward: "+10 XP",
      questions: { mood: "Comment te sens-tu ?", time: "Combien de temps as-tu ?", goal: "Ton objectif du jour ?" },
      answers: { great: "En forme !", ok: "Bof", tired: "Fatigué", t15: "15 min", t30: "30 min", t60: "60+ min", review: "Réviser", learn: "Apprendre du nouveau", test: "Préparer un contrôle" },
      reactions: {
        great: "Super ! Visons le plus grand défi du jour 🚀",
        ok: "Très bien : on commence par un défi court 💛",
        tired: "Pas de souci : 15 minutes suffisent pour la série 🌙",
        t15: "On choisit un défi éclair.",
        t30: "Bon rythme : une demi-heure rapporte des XP.",
        t60: "Waouh ! Tu peux tout terminer aujourd'hui.",
        review: "Rappel actif : cartes et quiz sont parfaits.",
        learn: "Nouveaux sujets : cartes et notes t'aident.",
        test: "Mode contrôle : je te guide pas à pas.",
      },
    },
    game: { questsDone: "Défis réussis", todayXp: "XP du jour", bonusTitle: "Bonus journée parfaite", bonusHint: "Termine tous les défis pour le bonus.", go: "Aller" },
    restCal: { title: "Calendrier de repos", open: "Choisir des dates", subtitle: "Touche les jours sans étude : la série reste protégée.", weekdays: "Jours fixes", dates: "Dates précises", selected_one: "{{count}} date choisie", selected_other: "{{count}} dates choisies", clear: "Effacer", save: "Enregistrer", cancel: "Annuler", saved: "Jours de repos mis à jour" },
    reminders: { title: "Rappels d'étude", open: "Notifications", subtitle: "Je te rappelle d'étudier et de garder ta série.", enable: "Activer les rappels", times: "Horaires", addTime: "Ajouter un horaire", days: "Jours", streakGuard: "Alerte série en danger (soir)", test: "Tester la notification", save: "Enregistrer", cancel: "Annuler", saved: "Rappels mis à jour", denied: "Permission refusée : active-la dans les réglages.", unsupported: "Cet appareil ne gère pas les notifications.", webHint: "Sur le web, l'app doit être ouverte ; sur mobile aussi en arrière-plan.", pushTitle: "C'est l'heure d'étudier ✨", pushBody: "15 minutes avec Lumen font grandir ta série.", streakTitle: "Ta série est en danger 🔥", streakBody: "Étudie aujourd'hui pour la garder." },
  },
  de: {
    checkin: {
      title: "Check-in mit Lumen",
      subtitle: "Antworte und schalte die Challenges frei",
      done: "Check-in erledigt! Viel Erfolg ✨",
      reward: "+10 XP",
      questions: { mood: "Wie fühlst du dich heute?", time: "Wie viel Zeit hast du?", goal: "Was ist dein Ziel heute?" },
      answers: { great: "Voll motiviert!", ok: "Geht so", tired: "Müde", t15: "15 Min", t30: "30 Min", t60: "60+ Min", review: "Wiederholen", learn: "Neues lernen", test: "Test vorbereiten" },
      reactions: {
        great: "Klasse! Nehmen wir die größte Challenge 🚀",
        ok: "Alles gut: Wir starten klein 💛",
        tired: "Kein Problem: 15 Minuten retten die Serie 🌙",
        t15: "Perfekt, eine Blitz-Challenge.",
        t30: "Guter Rhythmus: Eine halbe Stunde bringt XP.",
        t60: "Wow! Heute schaffst du alle Challenges.",
        review: "Aktives Wiederholen: Karten und Quiz sind ideal.",
        learn: "Neue Themen: Mindmaps und Notizen helfen.",
        test: "Test-Modus: Ich führe dich Schritt für Schritt.",
      },
    },
    game: { questsDone: "Challenges geschafft", todayXp: "XP heute", bonusTitle: "Bonus für den perfekten Tag", bonusHint: "Schließe alle Challenges für den Bonus ab.", go: "Los" },
    restCal: { title: "Ruhe-Kalender", open: "Daten wählen", subtitle: "Tippe die Tage ohne Lernen an: die Serie bleibt sicher.", weekdays: "Feste Wochentage", dates: "Einzelne Daten", selected_one: "{{count}} Datum gewählt", selected_other: "{{count}} Daten gewählt", clear: "Löschen", save: "Speichern", cancel: "Abbrechen", saved: "Ruhetage aktualisiert" },
    reminders: { title: "Lern-Erinnerungen", open: "Mitteilungen", subtitle: "Ich erinnere dich ans Lernen und an deine Serie.", enable: "Erinnerungen aktivieren", times: "Uhrzeiten", addTime: "Uhrzeit hinzufügen", days: "Tage", streakGuard: "Warnung: Serie in Gefahr (abends)", test: "Mitteilung testen", save: "Speichern", cancel: "Abbrechen", saved: "Erinnerungen aktualisiert", denied: "Berechtigung verweigert: in den Geräteeinstellungen aktivieren.", unsupported: "Dieses Gerät unterstützt keine Mitteilungen.", webHint: "Im Web nur bei geöffneter App; mobil auch im Hintergrund.", pushTitle: "Zeit zu lernen ✨", pushBody: "15 Minuten mit Lumen lassen deine Serie wachsen.", streakTitle: "Deine Serie ist in Gefahr 🔥", streakBody: "Lerne heute, damit die Serie bleibt." },
  },
  pt: {
    checkin: {
      title: "Check-in com o Lumen",
      subtitle: "Responde e desbloqueia os desafios de hoje",
      done: "Check-in feito! Bons estudos ✨",
      reward: "+10 XP",
      questions: { mood: "Como te sentes hoje?", time: "Quanto tempo tens?", goal: "Qual é o teu objetivo hoje?" },
      answers: { great: "Com energia!", ok: "Mais ou menos", tired: "Cansado", t15: "15 min", t30: "30 min", t60: "60+ min", review: "Rever", learn: "Aprender coisas novas", test: "Preparar um teste" },
      reactions: {
        great: "Boa! Vamos ao maior desafio de hoje 🚀",
        ok: "Tudo bem: começamos com um desafio curto 💛",
        tired: "Sem problema: 15 minutos mantêm a sequência 🌙",
        t15: "Escolhemos um desafio rápido.",
        t30: "Bom ritmo: meia hora já vale XP.",
        t60: "Uau! Hoje podes completar tudo.",
        review: "Revisão ativa: cartões e questionários são ideais.",
        learn: "Temas novos: mapas e apontamentos ajudam.",
        test: "Modo teste: guio-te passo a passo.",
      },
    },
    game: { questsDone: "Desafios concluídos", todayXp: "XP de hoje", bonusTitle: "Bónus dia perfeito", bonusHint: "Completa todos os desafios para o bónus.", go: "Ir" },
    restCal: { title: "Calendário de descanso", open: "Escolher datas", subtitle: "Toca nos dias sem estudo: a sequência fica protegida.", weekdays: "Dias fixos da semana", dates: "Datas específicas", selected_one: "{{count}} data escolhida", selected_other: "{{count}} datas escolhidas", clear: "Limpar", save: "Guardar", cancel: "Cancelar", saved: "Dias de descanso atualizados" },
    reminders: { title: "Lembretes de estudo", open: "Notificações", subtitle: "Lembro-te de estudar e manter a sequência.", enable: "Ativar lembretes", times: "Horários", addTime: "Adicionar horário", days: "Dias", streakGuard: "Aviso de sequência em risco (noite)", test: "Testar notificação", save: "Guardar", cancel: "Cancelar", saved: "Lembretes atualizados", denied: "Permissão negada: ativa nas definições do dispositivo.", unsupported: "Este dispositivo não suporta notificações.", webHint: "Na web chegam com a app aberta; no telemóvel também em segundo plano.", pushTitle: "Hora de estudar ✨", pushBody: "15 minutos com o Lumen fazem a tua sequência crescer.", streakTitle: "A tua sequência está em risco 🔥", streakBody: "Estuda hoje para não perderes a sequência." },
  },
};
